import { Component, type ReactNode, type ErrorInfo } from "react";

interface ErrorBoundaryProps {
  fallback?: (error: Error, reset: () => void) => ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: 24,
            background: "var(--bg-surface)",
            color: "var(--text-primary)",
            fontFamily: "var(--sans)",
          }}
        >
          <div
            style={{
              maxWidth: 380,
              padding: "20px 24px",
              background: "var(--bg-raised)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--error)",
                marginBottom: 8,
              }}
            >
              Something went wrong
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-secondary)",
                marginBottom: 16,
                fontFamily: "var(--mono)",
                wordBreak: "break-word",
              }}
            >
              {error.message}
            </div>
            <button
              onClick={this.reset}
              style={{
                height: 28,
                padding: "0 14px",
                background: "var(--accent-dim)",
                color: "var(--text-bright)",
                border: "none",
                borderRadius: "var(--radius-sm)",
                fontFamily: "var(--sans)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
